import {
  CIVIL_EXTENSION_ID,
  type CaptureTarget,
  type ExtensionAccount,
  type ExtensionHello,
  type FromExtension,
  type ToExtension,
  type ToExtensionPort,
} from '@civil/schema';

/**
 * Civil's page and the Chrome extension (docs/ui-tab.md §9.2). The extension can be
 * reached only from Civil's origins, and Civil talks only to its fixed id. What the
 * extension's overlay captures and marks arrives here, in Civil's open tab, which saves
 * it through its own signed-in API and says what it saved: the extension holds no token.
 *
 * `chrome.runtime` exists on a page only when some installed extension lets that page
 * reach it; without Civil's, every call here answers "not installed".
 */

interface Port {
  onMessage: { addListener(cb: (message: unknown) => void): void };
  onDisconnect: { addListener(cb: () => void): void };
  postMessage(message: unknown): void;
  disconnect(): void;
}
interface Runtime {
  sendMessage(id: string, message: ToExtension, reply: (response: unknown) => void): void;
  connect(id: string, info: { name: string }): Port;
  lastError?: { message?: string };
}

const runtime = (): Runtime | undefined => {
  const r = (globalThis as { chrome?: { runtime?: Partial<Runtime> } }).chrome?.runtime;
  return r?.sendMessage && r.connect ? (r as Runtime) : undefined;
};

/** A browser that can run Civil's extension at all: Chrome, Edge, Brave, Arc, Opera. */
export const canRunExtension = (): boolean =>
  typeof window !== 'undefined' && 'chrome' in window && /Chrome\//.test(navigator.userAgent) && !/Mobile/.test(navigator.userAgent);

function send<T>(message: ToExtension, timeoutMs = 1500): Promise<T | null> {
  const r = runtime();
  if (!r) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    try {
      r.sendMessage(CIVIL_EXTENSION_ID, message, (response) => {
        clearTimeout(timer);
        // Reading lastError is what tells Chrome the failure was seen: not installed, or disabled.
        resolve(r.lastError ? null : ((response as T) ?? null));
      });
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

/** The extension, when it is installed and answers; with the account that connected it, when one did. */
export const helloExtension = (): Promise<ExtensionHello | null> =>
  send<ExtensionHello>({ type: 'hello' }).then((h) => (h?.ok ? h : null));

/** Connects it as this account: no secret changes hands. Its popup opens to say so, when Chrome lets it. */
export const pairExtension = (account: ExtensionAccount): Promise<{ ok: boolean; popup?: boolean } | null> =>
  send<{ ok: boolean; popup?: boolean }>({ type: 'pair', account }, 5000);

/** Opens the application with the overlay on it; `asking` when Chrome is first asking whether it may go on that site. */
export const openWithExtension = (
  url: string,
  target: CaptureTarget,
  phone: boolean,
): Promise<{ ok: boolean; engaged?: boolean; asking?: boolean; error?: string } | null> =>
  send<{ ok: boolean; engaged?: boolean; asking?: boolean; error?: string }>({ type: 'open', url, target, ...(phone ? { phone: true } : {}) }, 8000);

const isFromExtension = (m: unknown): m is FromExtension =>
  !!m && typeof m === 'object' && typeof (m as { id?: unknown }).id === 'string' &&
  ['snapshot', 'mark', 'mark-note', 'mark-remove'].includes(String((m as { type?: unknown }).type));

export interface ExtensionLink {
  /** Tells the extension where this tab is: where captures from tabs not opened here go. */
  here(target: CaptureTarget): void;
  close(): void;
}

/**
 * Holds a connection open to the extension and answers what it sends — each item saved by
 * `handle`, and an `ack` back with what it was saved as. The extension's worker sleeps when
 * idle and the connection with it; the page connects again, which wakes it.
 */
export function linkExtension(
  handle: (message: FromExtension) => Promise<{ saved?: string; requestId?: string } | null>,
): ExtensionLink {
  const r = runtime();
  let port: Port | null = null;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let lastTarget: CaptureTarget | null = null;
  const post = (message: ToExtensionPort) => {
    try {
      port?.postMessage(message);
    } catch {
      // Gone: the next connection says it again.
    }
  };
  const connect = () => {
    if (stopped || !r) return;
    try {
      port = r.connect(CIVIL_EXTENSION_ID, { name: 'civil' });
    } catch {
      return;
    }
    if (lastTarget) post({ type: 'here', target: lastTarget });
    port.onMessage.addListener((m) => {
      if (!isFromExtension(m)) return;
      void handle(m).then(
        (result) => {
          // null: not this tab's project — another Civil tab answers it.
          if (result) post({ type: 'ack', id: m.id, ok: true, ...(result.saved ? { saved: result.saved } : {}), ...(result.requestId ? { requestId: result.requestId } : {}) });
        },
        (error: Error) => post({ type: 'ack', id: m.id, ok: false, error: error.message }),
      );
    });
    port.onDisconnect.addListener(() => {
      void r.lastError;
      port = null;
      if (!stopped) timer = setTimeout(connect, 1000);
    });
  };
  connect();
  return {
    here(target) {
      lastTarget = target;
      post({ type: 'here', target });
    },
    close() {
      stopped = true;
      clearTimeout(timer);
      port?.disconnect();
    },
  };
}
